/**
 * Small, purpose-built CMCD v2 line/beacon-URL builders for the standalone
 * "waiting for a live stream to start" custom event, sent independently of
 * hls.js's own CMCDController (which never attaches while a stream isn't
 * playable, so its eventTargets beacon never fires during the wait). Not a
 * general CMCD encoder - just enough for this one line shape (RFC 8941
 * structured-field dictionary).
 */

/**
 * `e=ce,cen="waiting"` - see docs/cmcd-v2-event-contract.md's custom-event
 * convention. Keys are alphabetically sorted and `v=2` is appended to match
 * hls.js's own CMCDController (`@svta/cml-cmcd`'s `prepareCmcdData`, which
 * always appends `v` for v2 and sorts keys before serializing) - otherwise
 * this would be the only v-less, unsorted line in the /cmcd request stream.
 */
export function buildWaitingCmcdLine(
  sid: string,
  cid: string,
  now: number = Date.now(),
): string {
  return `cen="waiting",cid="${cid}",e=ce,sid="${sid}",ts=${now},v=2`;
}

/**
 * Throttle gate for the waiting-viewer beacon: true once at least
 * `intervalMs` has elapsed since `lastSentAt`. `lastSentAt: null` (never
 * sent) is always eligible, so the first call after a stream starts being
 * waited on fires immediately rather than waiting a full interval first.
 * `null` rather than a `0` sentinel: `now` is a real timestamp in
 * production (always far larger than any interval), but a sentinel that
 * collides with a legitimate time value is a foot-gun in tests/simulations
 * that use relative time starting at 0.
 */
export function shouldSendWaitingCmcdBeacon(
  lastSentAt: number | null,
  now: number,
  intervalMs: number,
): boolean {
  if (lastSentAt === null) return true;
  return now - lastSentAt >= intervalMs;
}

/**
 * Mirrors LiveStreamStatus (bunnynet-stream-player's
 * src/services/video-client.types.ts, server-side) - duplicated rather than
 * imported since the client bundle and the Deno server module graph are
 * separate. Keep in sync if the server enum changes.
 */
export const LiveStreamStatus = {
  Unknown: 0,
  Created: 1,
  Scheduled: 2,
  Preview: 3,
  Running: 4,
  Ended: 5,
  VodProcessing: 6,
  Error: 7,
} as const;

/**
 * LiveStreamStatus values that mean the stream hasn't started yet - the only
 * ones "waiting for it to start" actually describes. An allow-list rather
 * than excluding just the terminal statuses (Ended/VodProcessing/Error): it
 * also keeps Running (and Preview, which server-side isPlayable already
 * treats as playable) out of the waiting-viewer beacon without depending on
 * that coupling holding forever - !isPlayable alone would otherwise fire for
 * any future non-playable state, live or not.
 */
const PRE_START_LIVE_STREAM_STATUSES = new Set<number>([
  LiveStreamStatus.Unknown, // status not resolved yet
  LiveStreamStatus.Created, // stream created, broadcaster hasn't gone live
  LiveStreamStatus.Scheduled, // has a scheduled start time, not live yet
  LiveStreamStatus.Preview, // broadcaster is in preview, not publicly live yet
]);

/**
 * True only when a viewer is genuinely waiting for a live stream to start:
 * not playable yet, and in one of the pre-start statuses. Deliberately not
 * just `!isPlayable` - that alone would also be true for Running (e.g. a
 * transient playlist glitch) or a finished/broken stream, neither of which
 * is "waiting for it to start". A missing/unrecognized status (-1 fallback)
 * is treated as not-waiting, since the real endpoint always sends one.
 */
export function isWaitingForLiveStreamStart(
  isPlayable: boolean,
  status: number | null | undefined,
): boolean {
  if (isPlayable) return false;
  return PRE_START_LIVE_STREAM_STATUSES.has(status ?? -1);
}

/**
 * Same query-param convention #buildCmcdConfig() uses for the regular
 * hls.js-driven beacon. pullZoneId is not optional on the wire - the
 * analytics backend's /stream/analytics/cmcd requires it and 400s without
 * it, so videoLibraryId is never a substitute for a missing pullZoneId. Both
 * are sent together whenever present: pullZoneId as the required tenant
 * identifier, videoLibraryId as additional (optional, diagnostics-only)
 * context.
 */
export function buildCmcdBeaconUrl(
  locationHref: string,
  cid: string,
  options: { pullZoneId?: string | null; videoLibraryId?: string } = {},
): URL {
  const beaconUrl = new URL('/cmcd', locationHref);
  if (options.pullZoneId) {
    beaconUrl.searchParams.set('pullZoneId', options.pullZoneId);
  }
  if (options.videoLibraryId) {
    beaconUrl.searchParams.set('videoLibraryId', options.videoLibraryId);
  }
  beaconUrl.searchParams.set('cid', cid);
  return beaconUrl;
}
